import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CV_FILE_CONFIG } from 'app/shared/configs/file.config';
import { CvManagementService } from 'app/shared/services/data/cv/cv-management.service';
import { FileItem, FileLikeObject, FileUploader } from 'ng2-file-upload';
import { NgxSpinnerService } from 'ngx-spinner';
import { of, Subject, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import { PdfViewerModule } from 'ng2-pdf-viewer';

@Component({
  selector: 'app-cv-management',
  templateUrl: './cv-management.component.html',
  styleUrls: ['./cv-management.component.scss']
})
export class CvManagementComponent implements OnInit, OnChanges {
  public readonly ALLOWED_FILE_TYPES: string[] = CV_FILE_CONFIG.ALLOWED_FILE_TYPES
  public readonly ALLOWED_FILE_TYPES_TEXT: string[] = CV_FILE_CONFIG.ALLOWED_FILE_TYPES_TEXT.split(',')

  private selectedFile: File | null

  public uploader: FileUploader
  public hasDropZoneOver: boolean
  private cvBlob: Blob;
  public cvPreviewUrl: string | SafeResourceUrl

  @Input() public employeeId: number

  @Input() public resetCv$!: Subject<void>

  @Output() public onFileChange: EventEmitter<File | null> = new EventEmitter<File | null>()

  /**
   * Constructor
   *
   * @param {CvManagementService} cvManagementService
   * @param {DomSanitizer} domSanitizer
   * @param {NgxSpinnerService} spinner
   * 
  */
  constructor(
    private cvManagementService: CvManagementService,
    private domSanitizer: DomSanitizer,
    private spinner: NgxSpinnerService
  ) { }

  ngOnInit(): void {
    this.initializeValues()
  }

  ngOnChanges(changes: SimpleChanges): void {
    const employeeId = changes['employeeId']

    if (employeeId && employeeId.currentValue) this.loadCv()
  }

  private initializeValues(): void {
    this.subscribeToReset()

    this.createFileUploader()

    this.createUploaderEvents()

    this.hasDropZoneOver = false

    this.selectedFile = null
  }

  private subscribeToReset(): void {
    this.resetCv$.subscribe(() => this.discardFile())
  }

  private createFileUploader(): void {
    this.uploader = this.cvManagementService.createFileUploader()
  }

  private createUploaderEvents(): void {
    this.uploader.onAfterAddingFile = this.onAfterAddingFile

    this.uploader.onWhenAddingFileFailed = this.onWhenAddingFileFailed
  }

  private onAfterAddingFile = (item: FileItem): void => {
    const queue = this.uploader.queue

    if (queue.length > 1) queue.shift()

    const file = item._file

    const url = URL.createObjectURL(file)

    this.cvPreviewUrl = url

    this.selectedFile = file

    this.onFileChange.emit(file)
  }

  private onWhenAddingFileFailed = (item: FileLikeObject, filter: any, options: any): void => {
    const filterName = filter.name

    const fileType = item.type
    const fileSize = item.size

    const isInvalidType = filterName === 'mimeType' && !this.ALLOWED_FILE_TYPES.includes(fileType)

    if (isInvalidType) {
      const alertOptions: SweetAlertOptions = this.cvManagementService.invalidFileTypeAlert()

      this.fireSwalAlert(alertOptions)
    }

    const isInvalidSize = filterName === 'fileSize' && fileSize > CV_FILE_CONFIG.MAX_SIZE

    if (isInvalidSize) {
      const alertOptions: SweetAlertOptions = this.cvManagementService.invalidFileSizeAlert(item)

      this.fireSwalAlert(alertOptions)
    }
  }

  private loadCv(): void {
    this.spinner.show(undefined, {})

    this.cvManagementService.getCvByEmployeeId(this.employeeId)
      .pipe(
        catchError((err) => {
          if (err.status === 404) return of(null)

          return throwError(err)
        }),
        finalize(() => this.spinner.hide())
      )
      .subscribe({
        next: (data) => {
          if (!data) return
          data.arrayBuffer().then(buffer => {
            this.cvBlob = data;
            this.cvPreviewUrl = buffer;
          });

          const file = new File([data], 'cv.pdf', { type: data.type })

          this.setSelectedFile(file)
        },
        error: () => {
          const alertOptions = this.cvManagementService.unknowCvErrorAlert()

          this.fireSwalAlert(alertOptions)
        }
      })
  }

  public fileOverBase(e: any): void {
    this.hasDropZoneOver = e
  }

  public discardFile(): void {
    this.cvPreviewUrl = null

    this.uploader.clearQueue()

    this.setSelectedFile()
  }

  private setSelectedFile(file: File | null = null): void {
    this.selectedFile = file

    this.onFileChange.emit(file)
  }

  private fireSwalAlert(options: SweetAlertOptions): Promise<SweetAlertResult> {
    return this.cvManagementService.fireSwalAlert(options)
  }
}
